import _ from 'lodash';

import type { ErrorFieldType, RegieType } from '@/types';
import { isAdresseEmpty } from '../adresse';
import validateMail from './mail';

type ValidateFunctionType = (regie: RegieType) => ErrorFieldType[];

/**
 * Valide une régie
 */
const validateRegie: ValidateFunctionType = (regie) => {
  let errors: ErrorFieldType[] = [];

  errors = [...errors, ...validateRequired(regie)];

  // Si on a des champs obligatoires non renseignés, on ne va pas plus loin
  if (!_.isEmpty(errors)) return errors;

  errors = [...errors, ...validateFormat(regie)];

  return errors;
};

/**
 * Valide les champs obligatoires :
 * titre, collectivite, codeInterne, adresse, tresorerie (poste comptable)
 * + ribDepot, intituleCompteDepot si depotFondTresor === true
 */
const validateRequired = (regie: RegieType): ErrorFieldType[] => {
  const errors: ErrorFieldType[] = [];

  const {
    titre,
    collectivite,
    codeInterne,
    adresse,
    tresorerie,
    depotFondTresor,
    ribDepot,
    intituleCompteDepot,
  } = regie;

  if (_.isEmpty(titre)) {
    errors.push({ id: 'titre', message: 'Le nom de la régie est obligatoire' });
  }

  if (_.isEmpty(collectivite)) {
    errors.push({ id: 'collectivite', message: 'La collectivité est obligatoire' });
  }

  if (_.isEmpty(codeInterne)) {
    errors.push({ id: 'codeInterne', message: 'Le code interne est obligatoire' });
  }

  if (isAdresseEmpty(adresse)) {
    errors.push({ id: 'adresse', message: "L'adresse est obligatoire" });
  }

  if (_.isEmpty(tresorerie)) {
    errors.push({ id: 'tresorerie', message: 'Le poste comptable est obligatoire' });
  }

  if (depotFondTresor) {
    if (_.isEmpty(ribDepot)) {
      errors.push({ id: 'ribDepot', message: "L'IBAN est obligatoire lorsque le DFT est activé" });
    }
    if (_.isEmpty(intituleCompteDepot)) {
      errors.push({
        id: 'intituleCompteDepot',
        message: "L'intitulé du compte est obligatoire lorsque le DFT est activé",
      });
    }
  }

  return errors;
};

/**
 * Valide le format et les limites de taille des champs
 */
const validateFormat: ValidateFunctionType = (regie) => {
  let errors: ErrorFieldType[] = [];

  const {
    titre,
    collectivite,
    codeInterne,
    service,
    horairesOuverture,
    mail,
    website,
    seuilRelance,
    seuilRelanceAvantEcheance,
    seuilRecouvrement,
  } = regie;

  if (_.size(titre) > 45) {
    errors.push({ id: 'titre', message: 'Le nom de la régie ne doit pas dépasser 45 caractères' });
  }

  if (_.size(collectivite) > 150) {
    errors.push({
      id: 'collectivite',
      message: 'La collectivité ne doit pas dépasser 150 caractères',
    });
  }

  if (_.size(codeInterne) > 5) {
    errors.push({
      id: 'codeInterne',
      message: 'Le code interne ne doit pas dépasser 5 caractères',
    });
  }

  if (_.size(service) > 150) {
    errors.push({ id: 'service', message: 'Le service ne doit pas dépasser 150 caractères' });
  }

  if (_.size(horairesOuverture) > 300) {
    errors.push({
      id: 'horairesOuverture',
      message: "Les horaires d'ouverture ne doivent pas dépasser 300 caractères",
    });
  }

  if (!_.isEmpty(mail)) {
    errors = [...errors, ...validateMail(mail)];
  }

  if (_.size(website) > 1000) {
    errors.push({
      id: 'website',
      message: 'Le site internet ne doit pas dépasser 1000 caractères',
    });
  }

  if (!_.isNil(seuilRelance)) {
    const value = _.toNumber(seuilRelance);
    if (value < 0 || value > 99) {
      errors.push({
        id: 'seuilRelance',
        message: 'Le seuil de relance doit être compris entre 0 et 99',
      });
    }
  }

  if (!_.isNil(seuilRelanceAvantEcheance)) {
    const value = _.toNumber(seuilRelanceAvantEcheance);
    if (value < 0 || value > 99) {
      errors.push({
        id: 'seuilRelanceAvantEcheance',
        message: 'Le seuil de relance doit être compris entre 0 et 99',
      });
    }
  }

  if (!_.isNil(seuilRecouvrement)) {
    const value = _.toNumber(seuilRecouvrement);
    if (value < 0 || value > 99) {
      errors.push({
        id: 'seuilRecouvrement',
        message: 'Le seuil de recouvrement doit être compris entre 0 et 99',
      });
    }
  }

  return errors;
};

export default validateRegie;
